import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Sync an admin page subsection with `?tab=` (or another query key)
 * so sidebar deep-links and in-page tabs stay aligned.
 */
export function useAdminTabParam<T extends string>(
  validTabs: readonly T[],
  defaultTab: T,
  paramName = 'tab'
): [T, (next: T) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get(paramName);
  const tab = (validTabs as readonly string[]).includes(raw || '')
    ? (raw as T)
    : defaultTab;

  const setTab = useCallback(
    (next: T) => {
      const nextParams = new URLSearchParams(searchParams);
      if (next === defaultTab) {
        nextParams.delete(paramName);
      } else {
        nextParams.set(paramName, next);
      }
      setSearchParams(nextParams, { replace: true });
    },
    [defaultTab, paramName, searchParams, setSearchParams]
  );

  return [tab, setTab];
}
